import React, { FC } from "react"
import { graphql } from "gatsby"

import { BespokeComponentFragmentFragment } from "@graphqlTypes"

// CSS Wrapped Components
import AccordionBlock from "@components/lp/AccordionBlock/AccordionBlock"
import BentoBoxBlock from "@components/lp/BentoBoxBlock/BentoBoxBlock"
import ContentPlusMediaBlock from "@components/lp/ContentPlusMediaBlock/ContentPlusMediaBlock"
import CtaBlock from "@components/lp/CtaBlock/CtaBlock"
import FeatureGridBlock from "@components/lp/FeatureGridBlock/FeatureGridBlock"
import InlineCTA from "@components/lp/InlineCTA/InlineCTA"
import LogosBlock from "@components/lp/LogosBlock/LogosBlock"
import LpHero from "@components/lp/LpHero/LpHero"
import MediaBlock from "@components/lp/MediaBlock/MediaBlock"
import ResourcesBlock from "@components/lp/ResourcesBlock/ResourcesBlock"
import StatBlock from "@components/lp/StatBlock/StatBlock"
import TabsBlock from "@components/lp/TabsBlock/TabsBlock"
import TestimonialBlock from "@components/lp/TestimonialBlock/TestimonialBlock"
import TextBlock from "@components/lp/TextBlock/TextBlock"

// Bespoke Components
import CompetitorCalculatorSection from "./components/CompetitorCalculatorSection/CompetitorCalculatorSection"
import DelightLabsHero from "./components/DelightLabsHero/DelightLabsHero"
import DelightLabsMediaBlock from "./components/DelightLabsMediaBlock/DelightLabsMediaBlock"
import DelightLabsPrefooter from "./components/DelightLabsPrefooter/DelightLabsPrefooter"
import DelightLabsShareCarousel from "./components/DelightLabsShareCarousel/DelightLabsShareCarousel"
import HomeHero from "./components/HomeHero/HomeHero"
import HomeInboxTour from "./components/HomeInboxTour/HomeInboxTour"
import IntegrationsAppDirectory from "./components/IntegrationsAppDirectory/IntegrationsAppDirectory"
import IntegrationsFeaturedApps from "./components/IntegrationsFeaturedApps/IntegrationsFeaturedApps"
import MessagesFeatures from "./components/MessagesFeatures/MessagesFeatures"
import MobileHero from "./components/MobileHero/MobileHero"
import NewsletterFullWidthCta from "./components/NewsletterFullWidthCta/NewsletterFullWidthCta"
import PlatformHero from "./components/PlatformHero/PlatformHero"
import PricingBlock from "./components/PricingBlock/PricingBlock"
import PricingHero from "./components/PricingHero/PricingHero"
import PricingTable from "./components/PricingTable/PricingTable"
import SelfServiceHero from "./components/SelfServiceHero/SelfServiceHero"

export enum BESPOKE_COMPONENTS {
  CompetitorCalculatorSection = "CompetitorCalculatorSection",
  CssWrappedComponent = "CssWrappedComponent",
  DelightLabsHero = "DelightLabsHero",
  DelightLabsMediaBlock = "DelightLabsMediaBlock",
  DelightLabsPrefooter = "DelightLabsPrefooter",
  DelightLabsShareCarousel = "DelightLabsShareCarousel",
  HomeHero = "HomeHero",
  HomeInboxTour = "HomeInboxTour",
  IntegrationsAppDirectory = "IntegrationsAppDirectory",
  IntegrationsFeaturedApps = "IntegrationsFeaturedApps",
  MessagesFeatures = "MessagesFeatures",
  MobileHero = "MobileHero",
  MobilePrefooter = "MobilePrefooter",
  NewsletterFullWidthCta = "NewsletterFullWidthCta",
  PlatformHero = "PlatformHero",
  PlatformValuePropsStatBlock = "PlatformValuePropsStatBlock",
  PricingBlock = "PricingBlock",
  PricingHero = "PricingHero",
  PricingTable = "PricingTable",
  SelfServiceHero = "SelfServiceHero",
}

const BespokeComponent: FC<BespokeComponentFragmentFragment & {
  componentId: BESPOKE_COMPONENTS
}> = ({
  componentId,
  component,
  contentful_id,
  customCss,
  jsonPayload,
}) => {
    // CssWrappedComponent
    if (componentId === BESPOKE_COMPONENTS["CssWrappedComponent"]) {
      return (
        <>
          {customCss?.customCss && <style dangerouslySetInnerHTML={{ __html: customCss.customCss }} />}
          {component?.__typename === "ContentfulCta" && <CtaBlock {...component} />}
          {component?.__typename === "ContentfulLpAccordionBlock" && <AccordionBlock {...component} />}
          {component?.__typename === "ContentfulLpBentoBoxBlock" && <BentoBoxBlock {...component} />}
          {component?.__typename === "ContentfulLpContentPlusMediaBlock" && <ContentPlusMediaBlock {...component} />}
          {component?.__typename === "ContentfulLpFeatureGrid" && <FeatureGridBlock {...component} />}
          {component?.__typename === "ContentfulLpHero" && <LpHero {...component} />}
          {component?.__typename === "ContentfulLpInlineCta" && <InlineCTA {...component} />}
          {component?.__typename === "ContentfulLpLogosBlock" && <LogosBlock {...component} />}
          {component?.__typename === "ContentfulLpMediaBlock" && <MediaBlock {...component} />}
          {component?.__typename === "ContentfulLpResourcesBlock" && <ResourcesBlock {...component} />}
          {component?.__typename === "ContentfulLpStatBlock" && <StatBlock {...component} />}
          {component?.__typename === "ContentfulLpTabsBlock" && <TabsBlock {...component} />}
          {component?.__typename === "ContentfulLpTestimonialBlock" && <TestimonialBlock {...component} />}
          {component?.__typename === "ContentfulLpTextBlock" && <TextBlock {...component} />}
        </>
      )
    }

    // Everything else
    switch (componentId) {
      case BESPOKE_COMPONENTS["CompetitorCalculatorSection"]:
        return <CompetitorCalculatorSection />

      case BESPOKE_COMPONENTS["DelightLabsHero"]:
        return <DelightLabsHero {...{ contentful_id, jsonPayload }} />

      case BESPOKE_COMPONENTS["DelightLabsMediaBlock"]:
        return <DelightLabsMediaBlock {...{ contentful_id }} />

      case BESPOKE_COMPONENTS["DelightLabsPrefooter"]:
        return <DelightLabsPrefooter {...{ contentful_id, jsonPayload }} />

      case BESPOKE_COMPONENTS["DelightLabsShareCarousel"]:
        return <DelightLabsShareCarousel {...{ contentful_id, jsonPayload }} />

      case BESPOKE_COMPONENTS["HomeHero"]:
        return <HomeHero {...{ contentful_id, jsonPayload }} />

      case BESPOKE_COMPONENTS["HomeInboxTour"]:
        return <HomeInboxTour />

      case BESPOKE_COMPONENTS["IntegrationsAppDirectory"]:
        return <IntegrationsAppDirectory />

      case BESPOKE_COMPONENTS["IntegrationsFeaturedApps"]:
        return <IntegrationsFeaturedApps />

      case BESPOKE_COMPONENTS["MessagesFeatures"]:
        return <MessagesFeatures {...{ contentful_id, jsonPayload }} />

      case BESPOKE_COMPONENTS["MobileHero"]:
        return <MobileHero {...{ contentful_id, jsonPayload }} />

      case BESPOKE_COMPONENTS["NewsletterFullWidthCta"]:
        return <NewsletterFullWidthCta {...{ contentful_id, jsonPayload }} />

      case BESPOKE_COMPONENTS["PlatformHero"]:
        return <PlatformHero {...{ contentful_id, jsonPayload }} />

      case BESPOKE_COMPONENTS["PricingBlock"]:
        return <PricingBlock {...{ contentful_id, jsonPayload }} />

      case BESPOKE_COMPONENTS["PricingHero"]:
        return <PricingHero {...{ contentful_id, jsonPayload }} />

      case BESPOKE_COMPONENTS["PricingTable"]:
        return <PricingTable {...{ contentful_id, jsonPayload }} />

      case BESPOKE_COMPONENTS["SelfServiceHero"]:
        return <SelfServiceHero {...{ contentful_id, jsonPayload }} />

      default:
        return null
    }
  }

export const BespokeComponentFragment = graphql`
  fragment BespokeComponentFragment on ContentfulLpBespokeComponent {
    componentId
    component {
      __typename
      ... on ContentfulLpHero {
        ...LpHeroFragment
        entryTitle
      }
      ... on ContentfulCta {
        ...CtaFragment
        entryTitle
      }
      ... on ContentfulLpAccordionBlock {
        ...AccordionBlockFragment
        entryTitle
      }
      ... on ContentfulLpBentoBoxBlock {
        ...BentoBoxBlockFragment
        entryTitle
      }
      ... on ContentfulLpFeatureGrid {
        ...FeatureGridBlockFragment
        entryTitle
      }
      ... on ContentfulLpInlineCta {
        ...InlineCtaFragment
        entryTitle
      }
      ... on ContentfulLpTabsBlock {
        ...TabsBlockFragment
        entryTitle
      }
      ... on ContentfulLpLogosBlock {
        ...LogosBlockFragment
        entryTitle
      }
      ... on ContentfulLpContentPlusMediaBlock {
        ...ContentPlusMediaBlockFragment
        entryTitle
      }
      ... on ContentfulLpMediaBlock {
        ...MediaBlockFragment
        entryTitle
      }
      ... on ContentfulLpResourcesBlock {
        ...ResourcesBlockFragment
        entryTitle
      }
      ... on ContentfulLpStatBlock {
        ...StatBlockFragment
        entryTitle
      }
      ... on ContentfulLpTestimonialBlock {
        ...TestimonialBlockFragment
        entryTitle
      }
      ... on ContentfulLpTextBlock {
        ...TextBlockFragment
        entryTitle
      }
    }
    contentful_id
    customCss {
      customCss
    }
    jsonPayload {
      internal {
        content
      }
    }
  }
`

export default BespokeComponent
